import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMoneyBillWave, FaChartLine, FaCalendarAlt, FaTicketAlt } from 'react-icons/fa';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import { logout } from '../../../../utils/api/auth';
import OverviewCards from '../../../../components/OverviewCards.jsx';
import AnalyticsCharts from '../../../../components/AnalyticsCharts.jsx';
import CollapsibleEventsTable from '../../../../components/CollapsibleEventsTable.jsx';
import { fetchOrganiserRevenueMetrics } from '../../../../utils/api/organiserApi.js';
import '../css/RevenueDashboard.css';

const INITIAL_ANALYTICS_STATE = {
  totalRevenue: 0,
  monthlyRevenue: 0,
  averageRevenuePerEvent: 0,
  revenueGrowth: 0,
  monthlyTrends: [],
  revenueByCategory: [],
  topRevenueEvents: [],
  packagePerformance: [],
};

function OrganiserRevenueDashboard() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(INITIAL_ANALYTICS_STATE);

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[RevenueDashboard] 📊 Fetching — API: fetchOrganiserRevenueMetrics()');
      const response = await fetchOrganiserRevenueMetrics();
      console.log('[RevenueDashboard] ✅ Data loaded:', {
        columns: 'totalRevenue, monthlyRevenue, averageRevenuePerEvent, revenueGrowth, monthlyTrends[{month,revenue,events}], revenueByCategory, topRevenueEvents, packagePerformance',
        formula: 'totalRevenue = SUM(p."SoldTickets" * p."Price") * 0.98  [organiser 98% share of flat ticket price]',
        data: response,
      });
      setAnalyticsData(response);
    } catch (error) {
      console.error('[RevenueDashboard] ❌ Error fetching revenue data:', error);
      setError('Unable to load revenue data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout(nav);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Prepare data for components
  const overviewCards = [
    {
      icon: <FaMoneyBillWave />,
      title: 'Total Revenue',
      value: formatCurrency(analyticsData.totalRevenue),
      subtitle: 'All time earnings',
      color: '#EFB034FF'
    },
    {
      icon: <FaCalendarAlt />,
      title: 'This Month',
      value: formatCurrency(analyticsData.monthlyRevenue),
      subtitle: 'Monthly earnings',
      color: '#EFB034FF'
    },
    {
      icon: <FaChartLine />,
      title: 'Avg per Event',
      value: formatCurrency(analyticsData.averageRevenuePerEvent),
      subtitle: 'Revenue per event',
      color: '#EFB034FF'
    },
    {
      icon: <FaChartLine />,
      title: 'Growth Rate',
      value: `${analyticsData.revenueGrowth}%`,
      subtitle: 'Month over month',
      color: '#EFB034FF'
    }
  ];

  const charts = [
    {
      type: 'line',
      data: analyticsData.monthlyTrends,
      title: 'Revenue Trends (6 Months)',
      xAxisKey: 'month',
      lines: [
        { dataKey: 'revenue', color: '#EFB034FF', name: 'Revenue' },
        { dataKey: 'events', color: '#c3e1f8', name: 'Events' }
      ],
      height: 320,
      formatTooltip: (value) => formatCurrency(value)
    }
  ];

  if (loading) {
    return (
      <div className="payment-analytics">
        <GlobalHeader
          title="Revenue Dashboard"
          subtitle="Track your earnings and revenue performance"
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-analytics">
        <GlobalHeader
          title="Revenue Dashboard"
          subtitle="Track your earnings and revenue performance"
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <div className="loading-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="revenue-dashboard">
      <GlobalHeader
        title="Revenue Dashboard"
        subtitle="Track your earnings and revenue performance"
        onLogout={handleLogout}
        backTo="/organiser-dash"
      />

      <main className="revenue-dashboard__main">
        {/* Overview Cards */}
        <OverviewCards cards={overviewCards} />

        {/* Top Revenue Events - Collapsible */}
        <CollapsibleEventsTable 
          events={analyticsData.topRevenueEvents} 
          formatCurrency={formatCurrency} 
        />

        {/* Revenue Trends */}
        <AnalyticsCharts charts={charts} />

        </main>
    </div>
  );
}

export default OrganiserRevenueDashboard;
